import _classPrivateMethodInitSpec from "@babel/runtime/helpers/classPrivateMethodInitSpec";
import _classPrivateFieldInitSpec from "@babel/runtime/helpers/classPrivateFieldInitSpec";
import _classPrivateGetter from "@babel/runtime/helpers/classPrivateGetter";
import _assertClassBrand from "@babel/runtime/helpers/assertClassBrand";
import _classPrivateFieldSet from "@babel/runtime/helpers/classPrivateFieldSet2";
import _classPrivateFieldGet from "@babel/runtime/helpers/classPrivateFieldGet2";
import { getBasePath } from "@next-core/runtime";
import { escapeRegExp } from "lodash";
import { MessageService, RETRY_LIMIT } from "./MessageService.js";
var _ms = /*#__PURE__*/new WeakMap();
var _memoizedChannels = /*#__PURE__*/new WeakMap();
var _channelPayloads = /*#__PURE__*/new WeakMap();
var _MessageDispatcher_brand = /*#__PURE__*/new WeakSet();
export class MessageDispatcher {
  constructor() {
    _classPrivateMethodInitSpec(this, _MessageDispatcher_brand);
    _classPrivateFieldInitSpec(this, _ms, void 0);
    _classPrivateFieldInitSpec(this, _memoizedChannels, {
      sub: new Map(),
      unsub: new Map()
    });
    _classPrivateFieldInitSpec(this, _channelPayloads, new Map());
  }
  subscribe(channel, payload) {
    const stringifiedPayload = JSON.stringify(payload);
    return _assertClassBrand(_MessageDispatcher_brand, this, _subOrUnsub).call(this, "sub", channel, payload, stringifiedPayload);
  }
  unsubscribe(channel) {
    const stringifiedPayload = _classPrivateFieldGet(_channelPayloads, this).get(channel);
    if (!stringifiedPayload) {
      const msg = `The message channel to unsubscribe "${channel}" is not found`;
      // eslint-disable-next-line no-console
      console.error(msg);
      return Promise.reject(new Error(msg));
    }
    const payload = JSON.parse(stringifiedPayload);
    const result = _assertClassBrand(_MessageDispatcher_brand, this, _subOrUnsub).call(this, "unsub", channel, payload, stringifiedPayload);
    _classPrivateFieldGet(_memoizedChannels, this).sub.delete(stringifiedPayload);
    result.then(() => {
      _classPrivateFieldGet(_channelPayloads, this).delete(channel);
    });
    return result;
  }
  onMessage(channel, listener) {
    let stringifiedPayload = _classPrivateFieldGet(_channelPayloads, this).get(channel);
    let warned = false;
    return _classPrivateGetter(_MessageDispatcher_brand, this, _get_ms).onMessage(response => {
      if (!stringifiedPayload) {
        stringifiedPayload = _classPrivateFieldGet(_channelPayloads, this).get(channel);
      }
      if (!stringifiedPayload) {
        if (!warned) {
          // eslint-disable-next-line no-console
          console.error(`Message channel: "${channel}" not found`);
          warned = true;
        }
        return;
      }
      if (response.event === "MESSAGE.PUSH" && matchMessageChannel(stringifiedPayload, response.payload)) {
        listener(response.payload.message);
      }
    });
  }
  onClose(listener) {
    return _classPrivateGetter(_MessageDispatcher_brand, this, _get_ms).onClose(listener);
  }
  reset() {
    var _classPrivateFieldGet2;
    _classPrivateFieldGet(_memoizedChannels, this).sub.clear();
    _classPrivateFieldGet(_memoizedChannels, this).unsub.clear();
    _classPrivateFieldGet(_channelPayloads, this).clear();
    (_classPrivateFieldGet2 = _classPrivateFieldGet(_ms, this)) === null || _classPrivateFieldGet2 === void 0 || _classPrivateFieldGet2.reset();
  }
}
function _get_ms(_this) {
  if (!_classPrivateFieldGet(_ms, _this)) {
    const protocol = location.protocol === "https:" ? "wss:" : "ws:";
    const url = `${protocol}//${window.location.host}${getBasePath()}api/websocket_service/v1/ws`;
    _classPrivateFieldSet(_ms, _this, new MessageService(url));
  }
  return _classPrivateFieldGet(_ms, _this);
}
function _subOrUnsub(type, channel, payload, stringifiedPayload) {
  const hit = _classPrivateFieldGet(_memoizedChannels, this)[type].get(stringifiedPayload);
  if (hit) {
    return hit;
  }
  const request = {
    event: type === "sub" ? "TOPIC.SUB" : "TOPIC.UNSUB",
    payload
  };
  const promise = new Promise((resolve, reject) => {
    const identity = getIdentity(payload);
    const disposeOnMessage = _classPrivateGetter(_MessageDispatcher_brand, this, _get_ms).onMessage(response => {
      const isSuccess = response.event === (type === "sub" ? "TOPIC.SUB_SUCCESS" : "TOPIC.UNSUB_SUCCESS");
      const isFailed = response.event === (type === "sub" ? "TOPIC.SUB_FAILED" : "TOPIC.UNSUB_FAILED");
      if ((isSuccess || isFailed) &&
      // Put this after event type checks, to prevent unnecessary
      // JSON stringify.
      identity === getIdentity(response.payload)) {
        (isSuccess ? resolve : reject)(response);
        disposeOnMessage();
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        disposeOnClose();
      }
    });
    // istanbul ignore next: currently can't mock this
    const disposeOnClose = _classPrivateGetter(_MessageDispatcher_brand, this, _get_ms).onClose(() => {
      // V2 will emit error callback for sub/unsub each time connect failed,
      // while v3 will emit only once after retry limit exceeded.
      // Keep the `EVENT.detail.retryCount` for compatibility.
      reject({
        retryCount: RETRY_LIMIT
      });
      disposeOnMessage();
      disposeOnClose();
    });
    _classPrivateGetter(_MessageDispatcher_brand, this, _get_ms).send(request);
  });
  if (type === "sub") {
    _classPrivateFieldGet(_channelPayloads, this).set(channel, stringifiedPayload);
  }
  _classPrivateFieldGet(_memoizedChannels, this)[type].set(stringifiedPayload, promise);
  return promise;
}
function getIdentity(payload) {
  const {
    system,
    topic
  } = payload;
  return JSON.stringify({
    system,
    topic
  });
}
function matchMessageChannel(stringifiedPayload, responsePayload) {
  const payload = JSON.parse(stringifiedPayload);
  return payload.system === responsePayload.system && (
  // Exact match
  payload.topic === responsePayload.topic ||
  // Wildcards match
  // For `ab.cd.*`:
  // - Matches `ab.cd.r` or `ab.cd.x.yz`
  // - DOES NOT match `ab.cd.x/yz` or `ab.x.cd` or `x.ab.cd.yz`
  typeof payload.topic === "string" && payload.topic.includes("*") && new RegExp(`^${payload.topic.replace(/([^*]*)\*([^*]*)/g, (_m, p1, p2) => `${escapeRegExp(p1)}[^/]*${escapeRegExp(p2)}`)}$`).test(responsePayload.topic));
}
//# sourceMappingURL=MessageDispatcher.js.map